import { memo } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ProductCardSkeletonProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Skeleton matching ProductCard dimensions exactly to prevent layout shift.
 * Heights match: sm=112px, md=160px, lg=aspect-square
 */
export const ProductCardSkeleton = memo(function ProductCardSkeleton({
  size = 'md',
  className,
}: ProductCardSkeletonProps) {
  const imageSizeClasses = {
    sm: 'h-28', // 112px
    md: 'h-40', // 160px
    lg: 'aspect-square',
  };

  return (
    <div className={cn('flex-shrink-0', className)}>
      {/* Image skeleton */}
      <Skeleton 
        className={cn(
          'w-full rounded-xl',
          imageSizeClasses[size]
        )} 
      />
      
      {/* Text content skeleton */}
      <div className="mt-2.5 space-y-1.5">
        {/* Product name */}
        <Skeleton className="h-4 w-3/4" />
        {/* Store name */}
        <div className="flex items-center gap-1.5">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
});

interface ProductGridSkeletonProps {
  count?: number;
  className?: string;
}

/**
 * Grid of ProductCard skeletons for SavesPage loading state.
 */
export const ProductGridSkeleton = memo(function ProductGridSkeleton({
  count = 6,
  className,
}: ProductGridSkeletonProps) {
  return (
    <div className={cn('grid grid-cols-2 gap-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} size="lg" />
      ))}
    </div>
  );
});

export default ProductCardSkeleton;
